<script setup lang="ts">
import bellIcon from '@/assets/img/icon/bell.svg';
import NotificationCard from '@/modules/auth/components/notifications/NotificationCard.vue';
import type { Notification } from '@/modules/auth/types';
import type { Meta } from '@/types/DataResponse';
import AppTypography from '../base/AppTypography.vue';

const router = useRouter();
const notifications = ref<Notification[]>([]);
const meta = ref<Meta>();
const page = ref<number>(1);
const loading = ref(true);
const { data, getNotifications, read } = useNotifications();
const notificationStore = useNotificationStore();

onMounted(async () => {
  await handleFetchData();
  loading.value = false;
});

const handleReadNotification = async (notification: Notification) => {
  if (!notification.read) {
    await read(notification.id);
  }

  const index = notifications.value.findIndex((n) => n.id === notification.id);

  if (index !== -1) {
    notifications.value[index].read = true;
    notificationStore.decrementCount();
  }

  if (!notification.order_id) {
    return;
  }

  if (notification.notifiable && notification.notifiable.account_type === 'Customer') {
    router.push({
      name: 'customer-orders-id',
      params: {
        id: notification.order_id,
      },
    });

    return;
  }

  if (notification.notifiable && notification.notifiable.account_type === 'Driver') {
    router.push(`/driver/orders/${notification.order_id}`);
    return;
  }

  router.push({
    name: 'merchant-orders-id',
    params: {
      id: notification.order_id,
    },
  });
};

const handleFetchData = async () => {
  await getNotifications(page.value);

  if (data.value && page.value === 1) {
    notifications.value = data.value.data;
    meta.value = data.value.meta;
  } else {
    notifications.value = [...notifications.value, ...data.value!.data];
    meta.value = data.value!.meta;
  }
};

const handleLoadMore = async ({ done }: any) => {
  if (page.value >= (meta.value?.last_page || 0)) {
    done('empty');
    return;
  }

  page.value = page.value + 1;
  await handleFetchData();

  if (page.value >= (meta.value?.last_page || 0)) {
    done('empty');
  } else {
    done('ok');
  }
};
</script>

<template>
  <div class="h-100">
    <div class="text-center notifications">
      <div v-if="!loading">
        <div v-if="notifications.length === 0" class="notifications__empty">
          <div class="notifications__empty-state">
            <v-img :src="bellIcon" />
          </div>
          <AppTypography type="title-3" color="-secondary">No notifications</AppTypography>
        </div>

        <div v-else>
          <v-infinite-scroll class="notifications__list" @load="handleLoadMore" height="auto">
            <template v-for="notification in notifications">
              <NotificationCard :notification="notification" @click="handleReadNotification(notification)" />
            </template>

            <template #empty>
              <p class="text-caption">You're all caught up!</p>
            </template>
          </v-infinite-scroll>
        </div>
      </div>

      <div v-else class="d-flex justify-center align-center h-screen">
        <v-progress-circular indeterminate></v-progress-circular>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.notifications {
  display: flex;
  flex-direction: column;
  height: calc(100dvh - 60px);
  gap: 12px;
  overflow-y: auto;

  /* Thin scrollbar styling that overlays content */
  &::-webkit-scrollbar {
    width: 2px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    margin: 0;
  }

  &::-webkit-scrollbar-thumb {
    background: rgb(var(--v-theme-stroke));
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgb(var(--v-theme-stroke));
  }

  /* Firefox thin scrollbar - overlay mode */
  scrollbar-width: thin;
  scrollbar-color: rgb(var(--v-theme-stroke)) transparent;

  &:has(&__empty-state) {
    align-items: center;
    justify-content: center;
  }

  &__empty {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    row-gap: 16px;

    &-state {
      border-radius: 8px;
      width: 60px;
      height: 60px;
      padding: 10px;
      background-color: rgb(var(--v-theme-primary-2));
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  &__list {
    width: 100%;
    padding: 0 0 12px 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
}
</style>
